import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { DataTable, type Column } from '@/components/data-table/data-table';
import { Pagination } from '@/components/data-table/pagination';
import { TableToolbar } from '@/components/data-table/toolbar';
import { useDebounce } from '@/lib/hooks/use-debounce';
import { queryKeys } from '@/lib/query-keys';
import { formatDate, formatNumber } from '@/lib/format';
import { listUsers, type UserWithStats } from '@/features/users/api';
import { UserRoleBadge, UserStatusBadge } from '@/features/users/components/status-badge';
import { BulkCreateDialog } from '@/features/users/components/bulk-create-dialog';

const PAGE_SIZE = 25;

interface UsersSearch {
  search?: string;
  role?: string;
  sort?: string;
  page?: number;
}

const ROLE_FILTER = [
  { value: '', label: 'All roles' },
  { value: 'user', label: 'User' },
  { value: 'editor', label: 'Editor' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'admin', label: 'Admin' },
];

const SORT_OPTIONS = [
  { value: 'created_at:desc', label: 'Newest first' },
  { value: 'created_at:asc', label: 'Oldest first' },
  { value: 'lifetime_points:desc', label: 'Most points' },
  { value: 'last_seen_at:desc', label: 'Recently active' },
];

export const Route = createFileRoute('/_authenticated/users/')({
  validateSearch: (search: Record<string, unknown>): UsersSearch => ({
    search: typeof search['search'] === 'string' ? search['search'] : undefined,
    role: typeof search['role'] === 'string' ? search['role'] : undefined,
    sort: typeof search['sort'] === 'string' ? search['sort'] : undefined,
    page: typeof search['page'] === 'number' ? search['page'] : undefined,
  }),
  component: UsersPage,
});

function UsersPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_authenticated/users/' });
  const [searchText, setSearchText] = useState(search.search ?? '');
  const debounced = useDebounce(searchText, 350);
  const [bulkOpen, setBulkOpen] = useState(false);

  // Push debounced search into the URL (reset to page 1) when it changes.
  useEffect(() => {
    const current = search.search ?? '';
    if (debounced === current) return;
    void navigate({
      to: '/users',
      search: (prev) => ({ ...prev, search: debounced || undefined, page: 1 }),
    });
  }, [debounced]); // eslint-disable-line react-hooks/exhaustive-deps

  const sort = search.sort ?? 'created_at:desc';
  const [sortBy, sortOrder] = sort.split(':');
  const page = search.page ?? 1;

  const filters = {
    search: search.search,
    role: search.role,
    sort_by: sortBy,
    sort_order: sortOrder,
    page,
    limit: PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.users.list(filters),
    queryFn: () =>
      listUsers({
        search: search.search || undefined,
        role: search.role || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        page,
        limit: PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
  });

  function patchSearch(patch: Partial<UsersSearch>) {
    void navigate({ to: '/users', search: (prev) => ({ ...prev, ...patch }) });
  }

  const columns: Column<UserWithStats>[] = [
    {
      key: 'user',
      header: 'User',
      render: (u) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar user={u} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{u.display_name || 'Unnamed'}</div>
            {u.username ? <div className="wb-tiny">@{u.username}</div> : null}
          </div>
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (u) => (
        <div>
          {u.phone ? <div className="wb-mono wb-small">{u.phone}</div> : null}
          {u.email ? <div className="wb-tiny">{u.email}</div> : null}
          {!u.phone && !u.email ? <span className="wb-tiny">—</span> : null}
        </div>
      ),
    },
    { key: 'role', header: 'Role', render: (u) => <UserRoleBadge role={u.role} /> },
    { key: 'status', header: 'Status', render: (u) => <UserStatusBadge user={u} /> },
    {
      key: 'points',
      header: 'Points',
      align: 'right',
      render: (u) => <span className="wb-mono">{formatNumber(u.lifetime_points)}</span>,
    },
    {
      key: 'trips',
      header: 'Trips',
      align: 'right',
      render: (u) => <span className="wb-mono">{formatNumber(u.trips_completed)}</span>,
    },
    {
      key: 'joined',
      header: 'Joined',
      align: 'right',
      render: (u) => <span className="wb-small">{formatDate(u.created_at)}</span>,
    },
  ];

  return (
    <div className="mx-auto max-w-6xl p-8">
      <div className="flex flex-col gap-1.5" style={{ marginBottom: 20 }}>
        <span className="wb-eyebrow">People</span>
        <h1 className="wb-h1">Users</h1>
        <p className="wb-sub">Browse, search, and manage every WeeBee account.</p>
      </div>

      <div style={{ marginBottom: 16 }}>
        <TableToolbar
          search={searchText}
          onSearchChange={setSearchText}
          searchPlaceholder="Search name, phone, or email…"
          filters={
            <>
              <Select
                size="sm"
                options={ROLE_FILTER}
                value={search.role ?? ''}
                onChange={(e) => patchSearch({ role: e.target.value || undefined, page: 1 })}
                style={{ width: 150 }}
              />
              <Select
                size="sm"
                options={SORT_OPTIONS}
                value={sort}
                onChange={(e) => patchSearch({ sort: e.target.value, page: 1 })}
                style={{ width: 170 }}
              />
            </>
          }
          actions={
            <Button size="sm" variant="secondary" onClick={() => setBulkOpen(true)}>
              <UserPlus />
              Bulk create
            </Button>
          }
        />
      </div>

      <DataTable
        columns={columns}
        rows={query.data?.users}
        rowKey={(u) => u.id}
        isLoading={query.isLoading}
        error={query.error}
        onRetry={() => query.refetch()}
        onRowClick={(u) => navigate({ to: '/users/$userId', params: { userId: u.id } })}
        empty={{ title: 'No users found', hint: 'Try a different search or filter.' }}
      />

      {query.data && query.data.total > 0 ? (
        <Pagination
          page={query.data.page}
          limit={query.data.limit}
          total={query.data.total}
          onPageChange={(p) => patchSearch({ page: p })}
        />
      ) : null}

      <BulkCreateDialog open={bulkOpen} onClose={() => setBulkOpen(false)} />
    </div>
  );
}

function Avatar({ user }: { user: UserWithStats }) {
  const url = user.profile_photo_url || user.display_image_url;
  const initial = (user.display_name || user.username || '?').charAt(0).toUpperCase();
  return url ? (
    <img
      src={url}
      alt=""
      style={{ width: 34, height: 34, borderRadius: 999, objectFit: 'cover', flexShrink: 0 }}
    />
  ) : (
    <div
      aria-hidden
      style={{
        width: 34,
        height: 34,
        borderRadius: 999,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--honey-wash)',
        color: 'var(--honey-700)',
        fontWeight: 700,
        fontSize: 14,
      }}
    >
      {initial}
    </div>
  );
}
